/**
 * Extract a display filename from a storage URL.
 * Strips query/hash and common upload timestamp prefixes like `20240722_123456_`.
 * Matches mobile app cleaning behavior so admin shows the same name users uploaded.
 */
export const getFileNameFromUrl = url => {
  if (!url || typeof url !== 'string') return '';

  try {
    const pathname = new URL(url).pathname;
    const rawName = decodeURIComponent(pathname.split('/').filter(Boolean).pop() || '');
    return cleanUploadedFileName(rawName);
  } catch {
    const segment = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
    try {
      return cleanUploadedFileName(decodeURIComponent(segment));
    } catch {
      return cleanUploadedFileName(segment);
    }
  }
};

export const cleanUploadedFileName = fileName => {
  if (!fileName) return '';
  // Mobile uploads often prefix: YYYYMMDD_HHMMSS_originalname.ext
  const cleaned = fileName.replace(/^\d{8}_\d{6}_/, '');
  return cleaned || fileName;
};

export const getFileExtension = urlOrName => {
  const name = getFileNameFromUrl(urlOrName) || String(urlOrName || '');
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return parts
    .pop()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'heic',
  'heif',
  'avif',
  'tif',
  'tiff',
  'svg',
]);

export const isImageFile = urlOrName => {
  const ext = getFileExtension(urlOrName);
  return IMAGE_EXTENSIONS.has(ext);
};

export const isPdfFile = urlOrName => getFileExtension(urlOrName) === 'pdf';

export const getDocumentDisplayName = (url, baseName = 'Document') => {
  const nameWithoutExt = String(baseName || 'Document').replace(/\.[^.]+$/, '');
  const ext = getFileExtension(url);
  return ext ? `${nameWithoutExt}.${ext}` : nameWithoutExt;
};

const isUnsignedBlobUrl = url => {
  try {
    const parsed = new URL(url);
    return parsed.hostname.endsWith('.blob.core.windows.net') && !parsed.search;
  } catch {
    return false;
  }
};

/**
 * Link that opens an image or PDF directly in the browser.
 * PDFs used to go through Google's gview, which intermittently returns an empty
 * response that Chrome shows as a failed "gview" download, and images were drawn
 * into an about:blank tab. Signed (private) links already tell the browser to
 * display the file. Public links serve the stored type, and older uploads were
 * stored as octet-stream, which downloads; the backend redirects those to a
 * signed link that forces the type implied by the extension.
 */
export const getFileViewUrl = url =>
  isUnsignedBlobUrl(url)
    ? `${process.env.REACT_APP_SERVER_API_URL}/common/files/view?url=${encodeURIComponent(url)}`
    : url;

export const getDocumentViewHref = url => {
  if (!url) return '#';
  if (isImageFile(url) || isPdfFile(url)) {
    return getFileViewUrl(url);
  }
  return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(url)}`;
};

/**
 * Open document in a new tab.
 * - Images and PDFs: the browser itself (see getFileViewUrl)
 * - Anything else: Google Viewer
 */
export const openDocumentInNewTab = async (url, displayName = 'Document') => {
  if (!url || typeof window === 'undefined') {
    console.warn('[DocumentView] openDocumentInNewTab skipped — missing url or window');
    return;
  }

  const extension = getFileExtension(url);
  const isImage = isImageFile(url);
  const isPdf = isPdfFile(url);

  console.log('[DocumentView] View clicked', {
    displayName,
    extension,
    isImage,
    isPdf,
    url,
  });

  const href = getDocumentViewHref(url);
  console.log('[DocumentView] opening via', href);
  window.open(href, '_blank', 'noopener,noreferrer');
};
